import { Button } from '@/components/ui/Button';
import { useBillingDashboard } from '@/hooks/useBilling';
import {
  formatAmount,
  formatMonthYear,
  getMonthProgress,
} from '@/utils/billing.utils';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  FileText,
  Calendar,
  Euro,
  Users,
  ArrowRight,
  Info,
  Loader2,
} from 'lucide-react';

interface BillingDashboardProps {
  onViewActiveFolders: () => void;
  onViewInvoiceHistory: () => void;
}

export function BillingDashboard({
  onViewActiveFolders,
  onViewInvoiceHistory,
}: BillingDashboardProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useBillingDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border-red-200 bg-error-50 rounded-xl border p-6 text-center">
        <p className="text-error-700">
          {t('billing.error.loading', 'Erreur lors du chargement du dashboard')}
        </p>
      </div>
    );
  }

  const dashboard = data; // Le service retourne déjà BillingDashboardData après extraction de response.data.data
  const monthProgress = getMonthProgress();

  return (
    <div className="space-y-6">
      {/* Current Month Card */}
      <div className="border-l-primary-500 rounded-2xl border border-l-4 border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-xl font-bold text-neutral-900">
              <Users className="text-primary-600 h-5 w-5" />
              {t('billing.currentMonth.title', 'Mois en cours')}
            </h2>
            <span className="text-sm font-medium text-neutral-600">
              {formatMonthYear(
                dashboard.currentMonth.month,
                dashboard.currentMonth.year
              )}
            </span>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-primary-500 h-2 w-2 rounded-full" />
                <p className="text-primary-600 text-xs font-semibold tracking-wide uppercase">
                  {t('billing.currentMonth.folders', 'Dossiers actifs')}
                </p>
              </div>
              <p className="text-3xl font-bold text-neutral-900">
                {dashboard.currentMonth.uniqueFoldersCount}
              </p>
            </div>

            <div className="rounded-xl bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-success-500 h-2 w-2 rounded-full" />
                <p className="text-success-600 text-xs font-semibold tracking-wide uppercase">
                  {t('billing.currentMonth.estimated', 'Coût estimé')}
                </p>
              </div>
              <p className="text-3xl font-bold text-neutral-900">
                {formatAmount(dashboard.currentMonth.estimatedAmountTTC)}
              </p>
            </div>

            <div className="rounded-xl bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-warning-500 h-2 w-2 rounded-full" />
                <p className="text-warning-600 text-xs font-semibold tracking-wide uppercase">
                  {t('billing.currentMonth.progress', 'Progression')}
                </p>
              </div>
              <div className="flex items-end gap-2">
                <p className="text-3xl font-bold text-neutral-900">
                  {monthProgress}%
                </p>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="bg-warning-500 h-full rounded-full transition-all"
                  style={{ width: `${monthProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Vault Metrics */}
          {dashboard.currentMonth.vaultUsage && (
            dashboard.currentMonth.vaultUsage.activeVaultsCount > 0 ||
            dashboard.currentMonth.vaultUsage.documentsUploadedCount > 0
          ) && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-neutral-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="bg-violet-500 h-2 w-2 rounded-full" />
                  <p className="text-violet-600 text-xs font-semibold tracking-wide uppercase">
                    {t('billing.currentMonth.vaults', 'Dossiers avec coffre-fort')}
                  </p>
                </div>
                <p className="text-3xl font-bold text-neutral-900">
                  {dashboard.currentMonth.vaultUsage.activeVaultsCount}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {formatAmount(dashboard.currentMonth.vaultUsage.vaultActivationAmountHT)} HT
                </p>
              </div>

              <div className="rounded-xl bg-neutral-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="bg-indigo-500 h-2 w-2 rounded-full" />
                  <p className="text-indigo-600 text-xs font-semibold tracking-wide uppercase">
                    {t('billing.currentMonth.documents', 'Documents déposés')}
                  </p>
                </div>
                <p className="text-3xl font-bold text-neutral-900">
                  {dashboard.currentMonth.vaultUsage.documentsUploadedCount}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {formatAmount(dashboard.currentMonth.vaultUsage.documentUploadAmountHT)} HT
                </p>
              </div>

              <div className="rounded-xl bg-neutral-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="bg-purple-500 h-2 w-2 rounded-full" />
                  <p className="text-purple-600 text-xs font-semibold tracking-wide uppercase">
                    {t('billing.currentMonth.vaultTotal', 'Coût coffre-fort')}
                  </p>
                </div>
                <p className="text-3xl font-bold text-neutral-900">
                  {formatAmount(dashboard.currentMonth.vaultUsage.totalVaultAmountHT)}
                </p>
                <p className="mt-1 text-xs text-neutral-500">HT</p>
              </div>
            </div>
          )}

          <Button
            variant="outline"
            onClick={onViewActiveFolders}
            className="mt-4 w-full"
          >
            <FileText className="mr-2 h-4 w-4" />
            {t('billing.currentMonth.viewFolders', 'Voir les dossiers actifs')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="border-primary-200 bg-primary-50 rounded-xl border p-4">
        <div className="flex gap-3">
          <Info className="text-primary-600 h-5 w-5 flex-shrink-0" />
          <div>
            <h4 className="text-primary-900 mb-1 text-sm font-semibold">
              {t('billing.info.title', 'Comment ça marche ?')}
            </h4>
            <ul className="text-primary-700 list-disc space-y-1 pl-4 text-sm">
              <li>
                {t(
                  'billing.info.folders',
                  '1€ HT par dossier unique utilisé (1 fois ou 100 fois = 1€)'
                )}
              </li>
              <li>
                {t(
                  'billing.info.vaults',
                  '1€ HT par dossier disposant d\'un coffre-fort numérique'
                )}
              </li>
              <li>
                {t(
                  'billing.info.documents',
                  '0,25€ HT par document déposé dans un coffre-fort'
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Next Invoice */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3">
            <h3 className="flex items-center gap-2 text-base font-bold text-neutral-900">
              <Calendar className="text-primary-600 h-4 w-4" />
              {t('billing.nextInvoice.title', 'Prochaine facture')}
            </h3>
          </div>
          <p className="mb-2 text-xs text-neutral-600">
            {new Date(dashboard.nextInvoice.estimatedDate).toLocaleDateString(
              'fr-FR'
            )}
          </p>
          <p className="text-2xl font-bold text-neutral-900">
            {formatAmount(dashboard.nextInvoice.estimatedAmountTTC)}
          </p>
        </div>

        {/* Last Invoice */}
        {dashboard.lastInvoice && (
          <div className="rounded-xl border border-neutral-200 bg-white p-4">
            <div className="mb-3">
              <h3 className="flex items-center gap-2 text-base font-bold text-neutral-900">
                <FileText className="text-success-600 h-4 w-4" />
                {t('billing.lastInvoice.title', 'Dernière facture')}
              </h3>
            </div>
            <p className="mb-2 text-xs text-neutral-600">
              {formatMonthYear(
                dashboard.lastInvoice.month,
                dashboard.lastInvoice.year
              )}
            </p>
            <p className="text-2xl font-bold text-neutral-900">
              {formatAmount(dashboard.lastInvoice.totalAmountTTC)}
            </p>
          </div>
        )}

        {/* Year to Date */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3">
            <h3 className="flex items-center gap-2 text-base font-bold text-neutral-900">
              <Euro className="text-success-600 h-4 w-4" />
              {t('billing.yearToDate.title', 'Année en cours')}
            </h3>
          </div>
          <p className="mb-2 text-xs text-neutral-600">
            {t('billing.yearToDate.total', 'Total payé')}
          </p>
          <p className="text-2xl font-bold text-neutral-900">
            {formatAmount(dashboard.yearToDate.totalAmountPaid)}
          </p>
        </div>

        {/* Average Monthly */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-3">
            <h3 className="flex items-center gap-2 text-base font-bold text-neutral-900">
              <TrendingUp className="text-primary-600 h-4 w-4" />
              {t('billing.averageMonthly.title', 'Moyenne mensuelle')}
            </h3>
          </div>
          <p className="mb-2 text-xs text-neutral-600">
            {t('billing.averageMonthly.description', "Moyenne sur l'année")}
          </p>
          <p className="text-2xl font-bold text-neutral-900">
            {formatAmount(dashboard.yearToDate.averageMonthlyAmount)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          variant="secondary"
          onClick={onViewInvoiceHistory}
          className="flex-1"
        >
          <FileText className="mr-2 h-4 w-4" />
          {t('billing.viewHistory', "Voir l'historique des factures")}
        </Button>
      </div>
    </div>
  );
}
